import React, { useState, useCallback } from 'react';
import { uploadImage, deleteImage } from '../services/supabaseStorage';

const ImageUploader = ({ currentImageUrl, onImageUploaded, onImageRemoved, packetId = 'temp', questionNumber = 0 }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(currentImageUrl || null);
  const [dragActive, setDragActive] = useState(false);

  // Handle file selection
  const handleFileSelect = async (file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('გთხოვთ აირჩიოთ სურათის ფაილი');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('სურათი ძალიან დიდია (მაქს. 5MB)');
      return;
    }

    setError(null);
    setUploading(true);

    try {
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => setPreviewUrl(e.target.result);
      reader.readAsDataURL(file);

      // Upload to Supabase Storage
      const imageUrl = await uploadImage(file, packetId, questionNumber);
      
      // Update preview with actual URL
      setPreviewUrl(imageUrl);
      
      // Notify parent
      if (onImageUploaded) {
        onImageUploaded(imageUrl);
      }

    } catch (err) {
      console.error('Upload failed:', err);
      setError('ატვირთვა ვერ მოხერხდა: ' + err.message);
      setPreviewUrl(null);
    } finally {
      setUploading(false);
    }
  };

  // Handle file input change
  const handleChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  // Handle drag events
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  // Handle drop
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, [packetId, questionNumber]);

  // Handle remove
  const handleRemove = async () => {
    if (!previewUrl) return;

    if (window.confirm('დარწმუნებული ხართ, რომ გსურთ სურათის წაშლა?')) {
      // Delete from storage if it's a Supabase URL
      if (previewUrl.includes('supabase')) {
        await deleteImage(previewUrl);
      }

      setPreviewUrl(null);
      setError(null);

      if (onImageRemoved) {
        onImageRemoved();
      }
    }
  };

  return (
    <div style={styles.container}>
      <label style={styles.label}>სურათი (არასავალდებულო)</label>

      {!previewUrl ? (
        // Upload zone
        <div
          style={{
            ...styles.uploadZone,
            ...(dragActive ? styles.uploadZoneDrag : {}),
            ...(uploading ? styles.uploadZoneUploading : {})
          }}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => !uploading && document.getElementById('image-input').click()}
        >
          {uploading ? (
            <>
              <div style={styles.spinner} />
              <p>იტვირთება...</p>
            </>
          ) : (
            <>
              <div style={styles.uploadIcon}>🖼️</div>
              <p style={styles.uploadText}>
                ჩააგდეთ სურათი აქ ან დააჭირეთ არჩევისთვის
              </p>
              <p style={styles.uploadHint}>
                PNG, JPG, WebP (მაქს. 5MB)
              </p>
            </>
          )}

          <input
            id="image-input"
            type="file"
            accept="image/*"
            onChange={handleChange}
            style={{ display: 'none' }}
            disabled={uploading}
          />
        </div>
      ) : (
        // Preview zone
        <div style={styles.previewContainer}>
          <img
            src={previewUrl}
            alt="Preview"
            style={styles.previewImage}
            onError={(e) => {
              e.target.style.display = 'none';
              setError('სურათის ჩატვირთვა ვერ მოხერხდა');
            }}
          />
          <button
            type="button"
            style={styles.removeButton}
            onClick={handleRemove}
            disabled={uploading}
          >
            ✕ სურათის წაშლა
          </button>
        </div>
      )}

      {error && (
        <div style={styles.error}>
          ⚠️ {error}
        </div>
      )}

      <div style={styles.info}>
        💡 სურათი შეკუმშულ ფორმატში (WebP) აიტვირთება სერვერზე
      </div>
    </div>
  );
};

const styles = {
  container: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '500',
    fontSize: '14px',
    color: '#2d3748',
  },
  uploadZone: {
    border: '2px dashed #cbd5e0',
    borderRadius: '8px',
    padding: '40px 20px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s',
    background: '#f8f9fa',
  },
  uploadZoneDrag: {
    borderColor: '#667eea',
    background: '#eef2ff',
  },
  uploadZoneUploading: {
    cursor: 'not-allowed',
    opacity: 0.6,
  },
  uploadIcon: {
    fontSize: '3em',
    marginBottom: '15px',
  },
  uploadText: {
    margin: '10px 0',
    fontSize: '16px',
    color: '#2d3748',
  },
  uploadHint: {
    margin: '5px 0',
    fontSize: '13px',
    color: '#718096',
  },
  spinner: {
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #667eea',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 15px',
  },
  previewContainer: {
    textAlign: 'center',
    padding: '15px',
    background: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #dee2e6',
  },
  previewImage: {
    maxWidth: '100%',
    maxHeight: '300px',
    borderRadius: '6px',
    marginBottom: '15px',
    border: '1px solid #dee2e6',
  },
  removeButton: {
    background: '#f44336',
    color: 'white',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
  },
  error: {
    marginTop: '10px',
    padding: '10px',
    background: '#fff5f5',
    border: '1px solid #feb2b2',
    borderRadius: '6px',
    color: '#c53030',
    fontSize: '14px',
  },
  info: {
    marginTop: '8px',
    fontSize: '12px',
    color: '#718096',
    fontStyle: 'italic',
  },
};

// Add spinner animation
const styleSheet = document.styleSheets[0];
const keyframes = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
styleSheet.insertRule(keyframes, styleSheet.cssRules.length);

export default ImageUploader;